import type { ProjectTask } from '../types/project';
import {
  addCalendarDays,
  calendarDateFromApi,
  parseCalendarDateInput,
  todayBusinessCalendarDate,
} from './dateTime';

export type CompletedOnChoice = 'yesterday' | 'today' | 'custom';

export type CompletedTaskGroup = {
  date: string;
  heading: string;
  tasks: ProjectTask[];
};

export function yesterdayBusinessCalendarDate(now = new Date()): string {
  return (
    addCalendarDays(todayBusinessCalendarDate(now), -1) ??
    todayBusinessCalendarDate(now)
  );
}

export function resolveCompletedDate(
  choice: CompletedOnChoice,
  customDate: string,
  now = new Date(),
): string | null {
  const today = todayBusinessCalendarDate(now);
  if (choice === 'today') return today;
  if (choice === 'yesterday') return yesterdayBusinessCalendarDate(now);
  return parseCalendarDateInput(customDate) ? customDate.trim() : null;
}

export function completedDateFieldError(
  choice: CompletedOnChoice,
  customDate: string,
  now = new Date(),
): string | undefined {
  const resolved = resolveCompletedDate(choice, customDate, now);
  if (!resolved) return 'Select the date this task was completed.';
  const today = todayBusinessCalendarDate(now);
  if (resolved > today) {
    return 'Completed date cannot be in the future.';
  }
  return undefined;
}

export function formatCompletedCardDate(value: string | null | undefined): string {
  const dateOnly = calendarDateFromApi(value);
  const date = dateOnly ? parseCalendarDateInput(dateOnly) : null;
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${date.getFullYear()}`;
}

export function completedDateGroupHeading(
  date: string,
  today = todayBusinessCalendarDate(),
): string {
  if (date === today) return 'Today';
  if (date === addCalendarDays(today, -1)) return 'Yesterday';
  const parsed = parseCalendarDateInput(date);
  if (!parsed) return date;
  const currentYear = parseCalendarDateInput(today)?.getFullYear();
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(
    parsed,
  );
  const dayMonth = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    ...(parsed.getFullYear() === currentYear ? {} : { year: 'numeric' as const }),
  }).format(parsed);
  return `${weekday}, ${dayMonth}`;
}

export function formatCompletedFocusDate(value: string): string {
  const date = parseCalendarDateInput(value);
  if (!date) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export function shiftCompletedFocusDate(
  selected: string,
  days: number,
  today = todayBusinessCalendarDate(),
): string {
  const next = addCalendarDays(selected, days);
  if (!next) return selected;
  if (next > today) return today;
  return next;
}

export function tasksCompletedOn(
  tasks: ProjectTask[],
  date: string,
): ProjectTask[] {
  return tasks.filter(
    task => calendarDateFromApi(task.completedDate) === date,
  );
}

export function completedFocusLanes(
  tasks: ProjectTask[],
  selectedDate: string,
): {
  previousDate: string;
  todayTasks: ProjectTask[];
  yesterdayTasks: ProjectTask[];
} {
  const previousDate = addCalendarDays(selectedDate, -1) ?? selectedDate;
  return {
    previousDate,
    todayTasks: tasksCompletedOn(tasks, selectedDate),
    yesterdayTasks: tasksCompletedOn(tasks, previousDate),
  };
}

export function groupTasksByCompletedDate(
  tasks: ProjectTask[],
  today = todayBusinessCalendarDate(),
): CompletedTaskGroup[] {
  const buckets = new Map<string, ProjectTask[]>();
  for (const task of tasks) {
    const date = calendarDateFromApi(task.completedDate) ?? today;
    const list = buckets.get(date) ?? [];
    list.push(task);
    buckets.set(date, list);
  }
  return [...buckets.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([date, grouped]) => ({
      date,
      heading: completedDateGroupHeading(date, today),
      tasks: grouped,
    }));
}
